import { ProtocolVersion, } from '@standardnotes/models';
import { CreateConsistentBase64JsonPayloadUseCase } from '../Utils/CreateConsistentBase64JsonPayload';
import { doesPayloadRequireSigning } from '../../V004AlgorithmHelpers';
import { GenerateAuthenticatedDataUseCase } from './GenerateAuthenticatedData';
import { GenerateEncryptedProtocolStringUseCase } from './GenerateEncryptedProtocolString';
import { GenerateSymmetricAdditionalDataUseCase } from './GenerateSymmetricAdditionalData';
import { isItemsKey } from '../../../../Keys/ItemsKey/ItemsKey';
import { isKeySystemItemsKey } from '../../../../Keys/KeySystemItemsKey/KeySystemItemsKey';
import { V004Algorithm } from '../../../../Algorithm';
import { DeriveHashingKeyUseCase } from '../Hash/DeriveHashingKey';
export class GenerateEncryptedParametersUseCase {
    constructor(crypto) {
        this.crypto = crypto;
        this.generateProtocolStringUseCase = new GenerateEncryptedProtocolStringUseCase(this.crypto);
        this.generateAuthenticatedDataUseCase = new GenerateAuthenticatedDataUseCase();
        this.generateAdditionalDataUseCase = new GenerateSymmetricAdditionalDataUseCase(this.crypto);
        this.encodeBase64DataUsecase = new CreateConsistentBase64JsonPayloadUseCase(this.crypto);
        this.deriveHashingKeyUseCase = new DeriveHashingKeyUseCase(this.crypto);
    }
    execute(payload, key, signingKeyPair) {
        if (doesPayloadRequireSigning(payload) && !signingKeyPair) {
            throw Error('Payload requires signing but no signing key pair was provided.');
        }
        const commonAuthenticatedData = this.generateAuthenticatedDataUseCase.execute(payload, key);
        const hashingKey = this.deriveHashingKeyUseCase.execute(key);
        const { contentKey, encryptedContentKey } = this.generateEncryptedContentKey(key, hashingKey, commonAuthenticatedData, signingKeyPair);
        const { encryptedContent } = this.generateEncryptedContent(payload, hashingKey, contentKey, commonAuthenticatedData, signingKeyPair);
        return {
            uuid: payload.uuid,
            content_type: payload.content_type,
            items_key_id: isItemsKey(key) || isKeySystemItemsKey(key) ? key.uuid : undefined,
            content: encryptedContent,
            enc_item_key: encryptedContentKey,
            version: ProtocolVersion.V004,
            key_system_identifier: payload.key_system_identifier,
            shared_vault_uuid: payload.shared_vault_uuid,
        };
    }
    generateEncryptedContent(payload, hashingKey, contentKey, commonAuthenticatedData, signingKeyPair) {
        const content = JSON.stringify(payload.content);
        const { additionalData } = this.generateAdditionalDataUseCase.execute(content, hashingKey, signingKeyPair);
        const encryptedContent = this.generateProtocolStringUseCase.execute(content, contentKey, this.encodeBase64DataUsecase.execute(commonAuthenticatedData), this.encodeBase64DataUsecase.execute(additionalData));
        return {
            encryptedContent,
        };
    }
    generateEncryptedContentKey(key, hashingKey, commonAuthenticatedData, signingKeyPair) {
        const contentKey = this.crypto.generateRandomKey(V004Algorithm.EncryptionKeyLength);
        const { additionalData } = this.generateAdditionalDataUseCase.execute(contentKey, hashingKey, signingKeyPair);
        const encryptedContentKey = this.generateProtocolStringUseCase.execute(contentKey, key.itemsKey, this.encodeBase64DataUsecase.execute(commonAuthenticatedData), this.encodeBase64DataUsecase.execute(additionalData));
        return {
            contentKey,
            encryptedContentKey,
        };
    }
}
